import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import { toast } from 'react-toastify'
import fetcher from '../../fetcher'
import { usePipelineStore } from './stores/pipelineStore'
import { SOURCES, TIERS } from './constants'
import { keepEscapeInOpenSelect } from './selectEscape'
import './NewLeadModal.scss'

const EMPTY = { name: '', phone_number: '', email: '', source: 'WEB', tier: 'WARM', stage: '', notes: '' }

export const personStatus = (p) => (p.is_client ? 'Client' : p.is_lead ? 'Lead' : 'Contact')

// Phone entry. The column is free-form and stores what the rep typed; nearly
// every existing crm_person row is XXX-XXX-XXXX, so that is the shape produced
// here — the formatted string is what gets submitted, not just what is shown.
const phoneDigits = (value) => (value || '').replace(/\D/g, '').slice(0, 10)

// No trailing dash: "310" stays "310" so backspace never fights the formatter.
const formatPhone = (value) => {
    const d = phoneDigits(value)
    if (d.length <= 3) return d
    if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`
    return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`
}

// Dashes follow the 3rd and 6th digit, so a caret that sat after n digits lands
// n characters in, plus the separators it has passed.
const caretAfterDigits = (n) => n + (n > 3 ? 1 : 0) + (n > 6 ? 1 : 0)

export const NewLeadModal = ({ open, onClose }) => {
    const [form, setForm] = useState(EMPTY)
    const [stages, setStages] = useState([])
    const [saving, setSaving] = useState(false)

    // Typeahead over existing people. When one is picked, we reuse that Person
    // for the opportunity instead of creating a duplicate.
    const [selectedPerson, setSelectedPerson] = useState(null)
    const [results, setResults] = useState([])
    const [searching, setSearching] = useState(false)
    const [showResults, setShowResults] = useState(false)
    const debounceRef = useRef(null)
    const blurRef = useRef(null)

    // The phone field rewrites what was typed, so the caret has to be put back
    // by hand — otherwise every mid-number edit jumps to the end.
    const phoneRef = useRef(null)
    const phoneCaretRef = useRef(null)

    useEffect(() => {
        if (!open) return
        setForm(EMPTY)
        setSelectedPerson(null)
        setResults([])
        setShowResults(false)
        fetcher.getCrmStages().then((res) => {
            const active = (res?.data || []).filter((s) => s.is_active).sort((a, b) => a.order - b.order)
            setStages(active)
            setForm((f) => ({ ...f, stage: active[0]?.id || '' }))
        })
    }, [open])

    // Debounced name search. Skips while a person is already selected.
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        const q = form.name.trim()
        if (selectedPerson || q.length < 2) {
            setResults([])
            setSearching(false)
            return
        }
        setSearching(true)
        debounceRef.current = setTimeout(async () => {
            try {
                const res = await fetcher.getCrmPeople(new URLSearchParams({ search: q }))
                setResults(res?.data?.results || [])
            } catch (e) {
                setResults([])
            } finally {
                setSearching(false)
            }
        }, 300)
        return () => debounceRef.current && clearTimeout(debounceRef.current)
    }, [form.name, selectedPerson])

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))

    const onPhoneChange = (e) => {
        let raw = e.target.value
        let caret = e.target.selectionStart ?? raw.length

        // Backspacing a dash leaves the digits unchanged, so reformatting would
        // put it straight back and the key would look dead. Take the digit it
        // was separating instead.
        const digitsUnchanged = phoneDigits(raw) === phoneDigits(form.phone_number)
        if (raw.length < form.phone_number.length && digitsUnchanged && caret > 0) {
            raw = raw.slice(0, caret - 1) + raw.slice(caret)
            caret -= 1
        }

        phoneCaretRef.current = caretAfterDigits(phoneDigits(raw.slice(0, caret)).length)
        setForm((f) => ({ ...f, phone_number: formatPhone(raw) }))
    }

    // Runs unconditionally: an edit the formatter rejects (an 11th digit) leaves
    // the value identical, and the caret still needs holding in place.
    useLayoutEffect(() => {
        if (phoneCaretRef.current === null) return
        phoneRef.current?.setSelectionRange(phoneCaretRef.current, phoneCaretRef.current)
        phoneCaretRef.current = null
    })

    const onNameChange = (e) => {
        const name = e.target.value
        // Editing the name after a match means they no longer want that person.
        if (selectedPerson) setSelectedPerson(null)
        setForm((f) => ({ ...f, name }))
        setShowResults(true)
    }

    const pickPerson = (p) => {
        setSelectedPerson(p)
        setForm((f) => ({ ...f, name: p.name || '', phone_number: p.phone_number || '', email: p.email || '', notes: p.notes || '' }))
        setResults([])
        setShowResults(false)
    }

    const clearPerson = () => {
        setSelectedPerson(null)
        setForm((f) => ({ ...f, phone_number: '', email: '', notes: '' }))
        setShowResults(true)
    }

    const submit = async () => {
        if (!selectedPerson && !form.name.trim()) {
            toast.error('Name is required.')
            return
        }
        setSaving(true)
        try {
            let personId = selectedPerson?.id
            if (!personId) {
                const personRes = await fetcher.createCrmPerson({
                    name: form.name.trim(),
                    phone_number: form.phone_number,
                    email: form.email,
                    notes: form.notes,
                })
                personId = personRes.data.id
            } else if (form.notes !== (selectedPerson.notes || '')) {
                // The box holds their Person.notes, so an edit rewrites theirs.
                // Compared, not truthy-checked, so clearing it still saves.
                await fetcher.updateCrmPerson(personId, { notes: form.notes })
            }
            await fetcher.createCrmOpportunity({
                person: personId,
                stage: form.stage || null,
                source: form.source,
                tier: form.tier,
                status: 'OPEN',
            })
            toast.success(selectedPerson ? 'Opportunity added.' : 'Lead created.')
            // Only the column the lead was filed under, plus the totals — the
            // rest of the board is untouched by this create.
            usePipelineStore.getState().refreshStages([form.stage])
            onClose()
        } catch (e) {
            toast.error('Could not create the lead.')
        } finally {
            setSaving(false)
        }
    }

    const dropdownOpen = showResults && !selectedPerson && form.name.trim().length >= 2 && (searching || results.length > 0)

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            PaperProps={{ className: 'nl-modal', onKeyDown: keepEscapeInOpenSelect }}
        >
            <div className="nl-head">
                <div className="nl-head-ic"><AddIcon style={{ fontSize: 20 }} /></div>
                <div className="nl-head-text">
                    <div className="nl-title">New Lead</div>
                    <div className="nl-sub">Add a contact and drop them onto the pipeline.</div>
                </div>
                <button className="nl-x" onClick={onClose} aria-label="Close"><CloseIcon style={{ fontSize: 18 }} /></button>
            </div>

            <div className="nl-body">
                <div className="nl-field nl-typeahead">
                    <label className="nl-label">Full Name</label>
                    <input
                        value={form.name}
                        onChange={onNameChange}
                        onFocus={() => setShowResults(true)}
                        onBlur={() => { blurRef.current = setTimeout(() => setShowResults(false), 150) }}
                        placeholder="Full name"
                        autoFocus
                        autoComplete="off"
                    />
                    {dropdownOpen && (
                        <div className="nl-suggest" onMouseDown={(e) => e.preventDefault()}>
                            {searching && results.length === 0 && <div className="nl-suggest-empty">Searching…</div>}
                            {results.map((p) => (
                                <div key={p.id} className="nl-suggest-row" onClick={() => pickPerson(p)}>
                                    <div className="nl-suggest-main">
                                        <div className="nl-suggest-name">{p.name || 'Unnamed'}</div>
                                        <div className="nl-suggest-meta">{p.phone_number || p.email || '—'}</div>
                                    </div>
                                    <span className={`nl-suggest-tag tag-${personStatus(p).toLowerCase()}`}>{personStatus(p)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    {selectedPerson && (
                        <div className="nl-selected">
                            <span className="nl-selected-dot" />
                            <span className="nl-selected-text">
                                Existing <strong>{selectedPerson.name || 'person'}</strong> — a new opportunity
                                will be added to them.
                            </span>
                            <span className={`nl-suggest-tag tag-${personStatus(selectedPerson).toLowerCase()}`}>
                                {personStatus(selectedPerson)}
                            </span>
                            <button className="nl-selected-x" onClick={clearPerson} aria-label="Use a new person">
                                <CloseIcon style={{ fontSize: 14 }} />
                            </button>
                        </div>
                    )}
                </div>
                <div className="nl-row">
                    <div className="nl-field">
                        <label className="nl-label" htmlFor="nl-phone">Phone</label>
                        <input
                            id="nl-phone"
                            ref={phoneRef}
                            value={form.phone_number}
                            onChange={onPhoneChange}
                            placeholder="310-717-1875"
                            inputMode="tel"
                            readOnly={!!selectedPerson}
                        />
                    </div>
                    <div className="nl-field">
                        <label className="nl-label">Email</label>
                        <input value={form.email} onChange={set('email')} placeholder="name@email.com" readOnly={!!selectedPerson} />
                    </div>
                </div>
                <div className="nl-field">
                    <label className="nl-label">Stage</label>
                    <select value={form.stage} onChange={set('stage')}>
                        {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                </div>
                <div className="nl-row">
                    <div className="nl-field">
                        <label className="nl-label">Source</label>
                        <select value={form.source} onChange={set('source')}>
                            {SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                    </div>
                    <div className="nl-field">
                        <label className="nl-label">Tier</label>
                        <select value={form.tier} onChange={set('tier')}>
                            {TIERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                    </div>
                </div>
                <div className="nl-field">
                    <label className="nl-label" htmlFor="nl-notes">Notes</label>
                    <textarea
                        id="nl-notes"
                        rows={3}
                        value={form.notes}
                        onChange={set('notes')}
                        placeholder="Add notes…"
                    />
                </div>
            </div>

            <div className="nl-foot">
                <button className="nl-btn-ghost" onClick={onClose}>Cancel</button>
                <button className="nl-btn-primary" onClick={submit} disabled={saving}>
                    {saving ? 'Saving…' : selectedPerson ? 'Add Opportunity' : 'Create Lead'}
                </button>
            </div>
        </Dialog>
    )
}
